/**
 * Field type detection utilities for identifying boolean fields and transforming values
 */

/**
 * Detects if a value is a boolean-like field (0, 1, null, empty string, undefined)
 * @param value - Value to check
 * @returns true if value is boolean-like, false otherwise
 */
export function isBooleanLike(value: any): boolean {
  return (
    value === 0 ||
    value === "0" ||
    value === 1 ||
    value === "1" ||
    value === null ||
    value === "" ||
    value === undefined
  );
}

/**
 * Auto-detects if a field contains only boolean-like values
 * Checks first 10 items to determine if field is boolean
 * @param data - Array of data objects
 * @param fieldName - Name of field to check
 * @returns true if field appears to be boolean, false otherwise
 */
export function isBooleanField(data: any[], fieldName: string): boolean {
  if (data.length === 0) return false;

  const sampleSize = Math.min(10, data.length);
  for (let i = 0; i < sampleSize; i++) {
    const value = data[i][fieldName];
    if (!isBooleanLike(value)) {
      return false;
    }
  }
  return true;
}

/**
 * Transform a value to display label, handling boolean and explicit label mapping
 * @param rawValue - Raw value to transform
 * @param labelMap - Optional map of raw values to display labels
 * @param fieldIsBooleanDetected - Whether field was detected as boolean
 * @returns Display label string, or undefined if no mapping found (caller should handle fallback)
 */
export function transformLabel(
  rawValue: any,
  labelMap?: Record<string | number, string>,
  fieldIsBooleanDetected: boolean = false
): string | undefined {
  const strValue = String(rawValue).toLowerCase();

  // First check explicit labelMap
  if (labelMap) {
    if (labelMap[rawValue] !== undefined) {
      return labelMap[rawValue];
    }
    if (labelMap[strValue] !== undefined) {
      return labelMap[strValue];
    }
  }

  // Transform boolean fields only when explicitly detected as boolean
  if (fieldIsBooleanDetected) {
    if (rawValue === 1 || rawValue === "1") return "Yes";
    if (rawValue === 0 || rawValue === "0") return "No";
    if (rawValue === null || rawValue === "" || rawValue === undefined) return "Unknown";
  }

  // Return undefined if no transformation applied; caller handles fallback
  return undefined;
}
